import * as _ from 'lodash';
import { OrganType } from "@nkr/enums/index";
import { ICenterAddress, ICenterAddressContract } from "@nkr/models/center-address";
import { AuthActionCreators, IGet } from "@nkr/store/authorization";
import { IAddUpdateCenterAddressResponse, ICenterAddressListResponse } from "@nkr/store/center";
import { useAppHookContext } from "@nkr/utils/rest-api";
import { useEffect, useState } from "react";
import { IBaseResponse } from '@nkr/models/base-response';

interface Results {
    loading: boolean,
    error: string,
    list: ICenterAddress[],
    fetchData: (centerId: number, organType?: OrganType) => void,
    updateAddress: (address: ICenterAddressContract) => void,
    deleteAddress: (addressId: number) => Promise<void>
}

const useCenterAddressList = (centerId: number, organType: OrganType): Results => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<ICenterAddress[]>([]);

    const { dispatch } = useAppHookContext();

    useEffect(() => {
        setLoading(true);
        setError('');

        fetchData(centerId, organType);
    }, [centerId, organType]);

    const fetchData = async (centerId: number, organType?: OrganType) => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/addresses?organType=${organType}`
        };

        try {
            const response: ICenterAddressListResponse = await dispatch(AuthActionCreators.apiGet(config));

            if (response && response.data.results && response.data.results.length > 0) {
                setList(response.data.results);
            } else {
                setList([]);
            }
        } catch (err) {
            setError(err);
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    const updateAddress = async (address: ICenterAddressContract) => {
        setLoading(true);
        setError('');

        let options = {
            url: `${__API__}/center/address`,
            data: address
        };

        try {
            const r: IAddUpdateCenterAddressResponse = await dispatch(address.id ? AuthActionCreators.apiPut(options) : AuthActionCreators.apiPost(options));

            if (r.data.success) {
                let newList = _.cloneDeep(list);

                if (address.id) {
                    newList = newList.map(x => {
                        if (x.id == address.id) {
                            return r.data.results;
                        }

                        return x;
                    });
                } else {
                    newList.push(r.data.results);
                }

                setList(newList);
            } else {
                setError('An error occured while updating address.');
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const deleteAddress = async (addressId: number) => {
        setLoading(true);
        setError('');

        let options = {
            url: `${__API__}/center/address/${addressId}`,
        };

        try {
            const r: IBaseResponse = await dispatch(AuthActionCreators.apiDelete(options));

            if (r.data.success) {
                let newList = _.cloneDeep(list).filter(x => {
                    return x.id != addressId;
                });

                setList(newList);
            } else {
                setError('An error occured while updating address.');
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }

    return { loading, error, list, fetchData, updateAddress, deleteAddress };
}

export default useCenterAddressList;